import React, {useContext, useState, useEffect} from 'react'
import { ElementContext } from '../ElementContext';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { deleteWhatsappApiTemplate, getWhatsappApiTemplates } from '../../helpers/whatsapp-api';
import { useAuth } from '@frontegg/react';
import Swal from 'sweetalert2';

Modal.setAppElement('#root');

const WAPIEditTemplateModal = () => {
    
    const { user } = useAuth();
    // ✅ AGREGAR templateData del contexto
    const { 
        WAPIEditTemplateModalOpen, 
        setWAPIEditTemplateModalOpen, 
        userConfiguration, 
        selectedWACredentials, 
        setWhatsappTemplates, 
        setApprovedTemplates,
        templateData // ✅ IMPORTANTE: Obtener del contexto
    } = useContext(ElementContext);
    
    const { t, i18n } = useTranslation();
    const [editMode, setEditMode] = useState(true);
    const [loading, setLoading] = useState(false);
    const [templateInfo, setTemplateInfo] = useState({
        name: '',
        language: '',
        category: '',
        status: '',
        header: '',
        body: '',
        footer: '',
        variables: []
    });

    // ✅ PROCESAR TEMPLATE DATA AL ABRIR EL MODAL
    useEffect(() => {
        if (WAPIEditTemplateModalOpen && templateData) {
            try {
                console.log('Template data recibido:', templateData);

                // Validar que tenga componentes
                if (!templateData.components || !Array.isArray(templateData.components)) {
                    console.error('❌ Template sin componentes válidos');
                    return;
                }

                // ✅ BUSCAR COMPONENTES POR TIPO (no por índice)
                const headerComponent = templateData.components.find(comp => comp.type === 'HEADER');
                const bodyComponent = templateData.components.find(comp => comp.type === 'BODY');
                const footerComponent = templateData.components.find(comp => comp.type === 'FOOTER');

                // Extraer texto de forma segura
                const headerText = headerComponent?.text || '';
                const bodyText = bodyComponent?.text || '';
                const footerText = footerComponent?.text || '';

                console.log('✅ Header:', headerText);
                console.log('✅ Body:', bodyText);
                console.log('✅ Footer:', footerText);

                // Buscar variables
                const variableMatches = bodyText.match(/\{\{\d+\}\}/g) || [];

                setTemplateInfo({
                    name: templateData.name || '',
                    language: templateData.language || '',
                    category: templateData.category || '',
                    status: templateData.status || '',
                    header: headerText,
                    body: bodyText,
                    footer: footerText,
                    variables: variableMatches
                });

            } catch (error) {
                console.error('❌ Error al procesar template:', error);
            }
        }
    }, [WAPIEditTemplateModalOpen, templateData]);

    const closeModal = () => {
        setTemplateInfo({
            name: '',
            language: '',
            category: '',
            status: '',
            header: '',
            body: '',
            footer: '',
            variables: []
        });
        setWAPIEditTemplateModalOpen(false);
    }

    const handleDeleteTemplate = async(e) => {
        e.preventDefault();
        
        // Confirmar eliminación
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: `¿Deseas eliminar la plantilla "${templateInfo.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) {
            return;
        }

        setLoading(true);

        try {
            const email = user.email;
            const allWACredentials = userConfiguration.whatsappAPIcredentials;
            const WACredentials = allWACredentials.find(cred => cred.phoneNumberId === selectedWACredentials);
            
            if (!WACredentials) {
                throw new Error('No se encontraron las credenciales');
            }

            const deleteReq = await deleteWhatsappApiTemplate(WACredentials, templateData.name);

            if (deleteReq.success) {
                // Actualizar lista de templates
                const whatsappApiTemplates = await getWhatsappApiTemplates(WACredentials);
                const allTemplates = whatsappApiTemplates.allTemplates || [];
                const approvedTemplates = whatsappApiTemplates.approvedTemplates || [];
                setWhatsappTemplates(allTemplates);
                setApprovedTemplates(approvedTemplates);

                Swal.fire({
                    icon: 'success',
                    title: t('success') || 'Éxito',
                    text: 'Plantilla eliminada correctamente',
                    timer: 2000
                });

                closeModal();
            } else {
                throw new Error(deleteReq.error || 'Error al eliminar');
            }

        } catch (error) {
            console.error('❌ Error al eliminar template:', error);
            Swal.fire({
                icon: 'error',
                title: t('error') || 'Error',
                text: error.message || 'No se pudo eliminar la plantilla'
            });
        } finally {
            setLoading(false);
        }
    }

    const handleCancel = (e) => {
        e.preventDefault();
        setEditMode(true);
        closeModal();
    }

    const getStatusBadge = (status) => {
        const statusConfig = {
            'APPROVED': { color: 'success', text: 'Aprobada' },
            'PENDING': { color: 'warning', text: 'Pendiente' },
            'REJECTED': { color: 'danger', text: 'Rechazada' },
            'DISABLED': { color: 'secondary', text: 'Deshabilitada' }
        };

        const config = statusConfig[status] || { color: 'secondary', text: status };

        return (
            <span className={`badge bg-${config.color} ms-2`}>
                {config.text}
            </span>
        );
    };

    return (
        <Modal
            isOpen={WAPIEditTemplateModalOpen}
            closeTimeoutMS={200}
            className="modal-usersettings"
            overlayClassName="modal-fondo"
            onRequestClose={closeModal}
        >
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className='text-center text-mediumblue mb-0 flex-grow-1'>
                    {t('templateDetails') || 'Detalles de Plantilla'}
                </h4>
                <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    aria-label="Close"
                ></button>
            </div>
            <hr />

            <form>
                {/* Nombre y Estado */}
                <div className="mb-3">
                    <label htmlFor="tempName" className="form-label template-labels mb-0">
                        {t('name') || 'Nombre'}
                    </label>
                    <div className='wa-message-box-modal py-2 px-2 d-flex justify-content-between align-items-center'>
                        <span>{templateInfo.name}</span>
                        {templateInfo.status && getStatusBadge(templateInfo.status)}
                    </div>
                </div>
                
                {/* Idioma */}
                <div className="mb-3">
                    <label htmlFor="tempLang" className="form-label template-labels mb-0">
                        {t('language') || 'Idioma'}
                    </label>
                    <div className='wa-message-box-modal py-2 px-2'>
                        <span>
                            {templateInfo.language.includes('es') ? (t('spanish') || 'Español') : (t('english') || 'Inglés')}
                        </span>
                    </div>
                </div>
                
                {/* Categoría */}
                <div className="mb-3">
                    <label htmlFor="tempCateg" className="form-label template-labels mb-0">
                        {t('category') || 'Categoría'}
                    </label>
                    <div className='wa-message-box-modal py-2 px-2'>
                        <span>{templateInfo.category}</span>
                    </div>
                </div>
                
                {/* Contenido del Mensaje */}
                <div className="mb-3">
                    <label htmlFor="tempMsg" className="form-label mb-0">
                        {t('bodyMessage') || 'Mensaje'}
                    </label>
                    
                    <div className='wa-message-box-modal'>
                        {/* Header - Solo si existe */}
                        {templateInfo.header && (
                            <h6 className='wa-header'>{templateInfo.header}</h6>
                        )}
                        
                        {/* Body - Siempre debe existir */}
                        <p className='wa-body'>{templateInfo.body}</p>
                        
                        {/* Footer - Solo si existe */}
                        {templateInfo.footer && (
                            <span className='wa-footer'>{templateInfo.footer}</span>
                        )}
                    </div>

                    {/* Mostrar variables si existen */}
                    {templateInfo.variables.length > 0 && (
                        <div className="mt-2">
                            <small className="text-muted">
                                <strong>Variables:</strong> {templateInfo.variables.join(', ')}
                            </small>
                        </div>
                    )}
                </div>

                {/* Alertas según estado */}
                {templateInfo.status === 'PENDING' && (
                    <div className="alert alert-warning" role="alert">
                        <small>
                            <strong>Pendiente:</strong> Esta plantilla está siendo revisada por Meta.
                        </small>
                    </div>
                )}

                {templateInfo.status === 'REJECTED' && (
                    <div className="alert alert-danger" role="alert">
                        <small>
                            <strong>Rechazada:</strong> Meta rechazó esta plantilla. No puede ser utilizada.
                        </small>
                    </div>
                )}

                {/* Botones */}
                <div className='mt-4 btn-group w-100'>
                    <button
                        className='btn btn-sm btn-danger'
                        onClick={handleDeleteTemplate}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                {t('deleting') || 'Eliminando'}...
                            </>
                        ) : (
                            t('delete') || 'Eliminar'
                        )}
                    </button>
                    <button
                        className='btn btn-sm btn-mediumblue'
                        onClick={handleCancel}
                        disabled={loading}
                    >
                        {t('close') || 'Cerrar'}
                    </button>
                </div>
            </form>
        </Modal>
    )
}

export default WAPIEditTemplateModal